import { Typography, Avatar, Stack, Tooltip, Chip } from "@mui/material";
import {
  TextField,
  DateField,
  FunctionField,
  ReferenceField,
  useRecordContext,
  useNotify,
  useDataProvider,
} from "react-admin";
import { DocumentActions } from "./DocumentActions";
import { DeleteImpactDialog } from "./DeleteImpactDialog";
import { useState } from "react";
import { stringToColor } from "@/components/common/fields/AvatarField";
import { composeDocumentLabel, composeDocumentLabelAria } from "@/utils/formatters/documentLabel";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { PaginatedDatagrid } from "@/components/ui/react-admin/PaginatedDatagrid";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import {
  AlertType,
  DocumentRead,
  DocumentRecordType,
  DocumentStatus,
  ResourceName,
  TransformationTemplate,
  User,
} from "@/types";
import DocumentEmpty from "./DocumentEmptyView";
import RecordTypeField from "./RecordTypeField";
import { useNavigate } from "react-router-dom";
import { isFailedStatus, isProcessingStatus } from "@/constants/documentStatus";

enum DocumentActionType {
  DELETE = "delete",
  RETRY = "retry",
  REPROCESS = "reprocess",
}

interface DocumentQueueProps {
  isLoading: boolean;
  onRefresh: () => void;
  documents?: DocumentRead[];
  recordType?: DocumentRecordType;
  /**
   * Server-side pagination passthrough. When omitted the queue paginates the
   * passed `documents` array client-side (default, used by the Create flows).
   * The Documents list page drives these from the react-admin list context so
   * paging/sorting/filtering happen on the server. Page is 0-based (MUI).
   */
  serverSide?: boolean;
  totalCount?: number;
  page?: number;
  onPageChange?: (newPage: number) => void;
  rowsPerPage?: number;
  onRowsPerPageChange?: (newRowsPerPage: number) => void;
}

/**
 * Uploaded By Field - renders the user data from ReferenceField
 */
const UploadedByContent = () => {
  const user = useRecordContext<User>();

  if (!user) {
    return (
      <Typography variant="body2" color="text.secondary">
        Unknown User
      </Typography>
    );
  }

  const displayName = user.full_name || user.preferred_name || user.email || "Unknown";
  const { bgColor, textColor } = stringToColor(displayName);
  const initials = displayName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        maxWidth: 180,
        overflow: "hidden",
      }}
    >
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: 12,
          bgcolor: bgColor,
          color: textColor,
          flexShrink: 0,
        }}
      >
        {initials}
      </Avatar>
      <Tooltip title={displayName} placement="top">
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayName}
        </Typography>
      </Tooltip>
    </Stack>
  );
};

/**
 * DocumentRead Queue Component - Shows documents sorted by upload date
 */
export const DocumentQueue = ({
  documents,
  isLoading,
  onRefresh,
  recordType,
  serverSide,
  totalCount,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
}: DocumentQueueProps) => {
  const navigate = useNavigate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const dataProvider = useDataProvider();
  // AC4 / ADR-044: gate the trash icon's visibility on canDelete (mirrors
  // DocumentShow) so a read-only partner never sees the delete entry point.
  const { canDelete, canEdit } = useResourcePermissions(ResourceName.DOCUMENTS);

  // The delete flow now routes through the delete-impact dialog (#5751): the
  // trash icon opens a preview of what a delete would remove/keep/block and the
  // reviewer chooses document-only vs. cascade there — no immediate delete here.
  const [deleteDialogDocId, setDeleteDialogDocId] = useState<string | null>(null);

  // Filter documents by record type if specified
  const filteredDocuments = recordType ? documents?.filter((d) => d.record_type === recordType) : documents;

  const handleDelete = (documentId: string) => {
    setDeleteDialogDocId(documentId);
  };

  const handleReprocess = async (
    documentId: string,
    originalClientId: string,
    selectedClientId?: string,
    transformationTemplate?: TransformationTemplate
  ) => {
    try {
      await dataProvider.create(`documents/${documentId}/reprocess`, {
        data: {
          client_id_ref: selectedClientId || originalClientId, // Always send one or the other
          transformation_template: transformationTemplate,
          reason: transformationTemplate
            ? "User-provided field mapping improvements"
            : selectedClientId
              ? "User selected client for reprocessing"
              : "Manual reprocess",
          force_reprocess: !transformationTemplate, // Force if no template changes
          field_mappings: {}, // Send empty object to satisfy handler
        },
      });
      notify("Document reprocessing started", { type: AlertType.SUCCESS });
      onRefresh();
    } catch (error) {
      notifyError(error);
    }
  };

  const handleDocumentAction = async (
    action: string,
    documentId: string,
    originalClientId: string,
    selectedClientId?: string,
    transformationTemplate?: TransformationTemplate
  ) => {
    switch (action) {
      case DocumentActionType.RETRY:
      case DocumentActionType.REPROCESS:
        await handleReprocess(documentId, originalClientId, selectedClientId, transformationTemplate);
        break;

      case DocumentActionType.DELETE:
        handleDelete(documentId);
        break;
    }
  };

  // In server-side mode, don't flash the empty state while a fetch is in flight
  // (data is briefly [] between pages/filters). Client-side callers keep the
  // original behavior.
  const empty = filteredDocuments?.length === 0;

  if (empty && (!serverSide || !isLoading)) {
    return <DocumentEmpty recordType={recordType} />;
  }

  const deleteDialogDoc = deleteDialogDocId ? filteredDocuments?.find((d) => d.id === deleteDialogDocId) : undefined;

  return (
    <>
      {deleteDialogDocId && (
        <DeleteImpactDialog
          open
          documentId={deleteDialogDocId}
          documentName={deleteDialogDoc?.name}
          onClose={() => setDeleteDialogDocId(null)}
          onDeleted={onRefresh}
        />
      )}
      <PaginatedDatagrid
        data={filteredDocuments || []}
        isLoading={isLoading}
        rowClick={(id) => `/documents/${id}/show`}
        sort={{ field: "uploaded_datetime", order: "DESC" }}
        initialPerPage={25}
        serverSide={serverSide}
        totalCount={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
      >
        <FunctionField
          label="Document Name"
          sortBy="name"
          render={(record: DocumentRead) => {
            const sheet = record.object_metadata?.sheet_name;
            const label = composeDocumentLabel(record);
            // Em-dash is hidden from AT; aria-label reads "<name>, sheet <sheet_name>"
            // so screen readers don't pronounce the dash. MSW-8 AC8.5.
            return sheet ? (
              <Typography variant="body2" aria-label={composeDocumentLabelAria(record)}>
                {record.name}
                <span aria-hidden="true"> — </span>
                {sheet}
              </Typography>
            ) : (
              <Typography variant="body2">{label}</Typography>
            );
          }}
        />
        <RecordTypeField source="record_type" label="Record Type" />
        <StatusChipField source="status" />
        <FunctionField
          label="Client"
          sortable={false}
          render={(record: DocumentRead) =>
            // A multi-client (BOB) document has no single bound client — each row
            // resolves its own — so the client_id_ref column would render blank.
            // Show an explicit indicator instead (#5309).
            record.object_metadata?.is_multi_client ? (
              <Tooltip title="Book-of-business file — each row is matched to its own client on load">
                <Chip label="Multi-client" size="small" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
              </Tooltip>
            ) : (
              <ReferenceField
                source="client_id_ref"
                reference={ResourceName.CLIENTS}
                label="Client"
                link="show"
                sortable={false}
              >
                <TextField source="client_legal_name" />
              </ReferenceField>
            )
          }
        />
        <ReferenceField
          source="vendor_id_ref"
          reference={ResourceName.VENDORS}
          label="Vendor"
          link="show"
          sortable={false}
        >
          <TextField source="company_name" />
        </ReferenceField>
        <ReferenceField
          source="uploaded_by_user_id_ref"
          reference={ResourceName.USERS}
          label="Uploaded By"
          link={false}
          sortable={false}
        >
          <UploadedByContent />
        </ReferenceField>
        <DateField source="uploaded_datetime" label="Uploaded" showTime />
        <FunctionField label="File Format" render={(record: DocumentRead) => record.file_format_name || "—"} />
        <FunctionField
          label="Actions"
          render={(record: DocumentRead) => {
            const isProcessing = isProcessingStatus(record.status);
            const isFailed = isFailedStatus(record.status);

            if (record.status === DocumentStatus.REVIEW_REQUIRED) {
              return (
                <DocumentActions
                  onReview={(id) => navigate(`/documents/${id}/show`)}
                  // #6920: reprocess was reachable ONLY from the isFailed branch
                  // below, so a reviewer following our own DOC-000001 /
                  // DOC-000003 advice ("reprocess the document") had nothing to
                  // click here. The API never required `failed` — ReprocessDocument
                  // has no current-status guard — so this was a UI gap, not a
                  // capability one.
                  //
                  // Gated on canEdit, not canDelete: a reprocess re-runs the
                  // document, it does not remove it.
                  //
                  // ADR-044 decides THAT permission checks go through
                  // useResourcePermissions; it does not map verbs to actions, so
                  // it is not a ruling on this choice (!4711 review). canEdit is
                  // a judgement, and the same judgement now applies to the
                  // isFailed branch below.
                  onRetry={
                    canEdit
                      ? (id, selectedClientId) =>
                          handleDocumentAction(DocumentActionType.REPROCESS, id, record.client_id_ref, selectedClientId)
                      : undefined
                  }
                  retryLabel="Reprocess"
                  onDelete={
                    canDelete
                      ? (id) => handleDocumentAction(DocumentActionType.DELETE, id, record.client_id_ref)
                      : undefined
                  }
                />
              );
            }

            if (isFailed) {
              return (
                <DocumentActions
                  // Same word and same gate as the REVIEW_REQUIRED branch above
                  // (!4711 review, [AEM-queue-label-gate-split]). This branch
                  // said "Retry Processing" and was ungated, so one column
                  // offered two different words for one action and
                  // `documents:edit` was not in fact required to reprocess.
                  //
                  // NOTE this ADDS a gate where there was none: a user with
                  // documents:show but not :edit loses the retry affordance on a
                  // failed document. That is the coherent end state — they
                  // already could not reprocess a review_required one — but it
                  // is a behaviour change, called out rather than slipped in.
                  onRetry={
                    canEdit
                      ? (id, selectedClientId) =>
                          handleDocumentAction(DocumentActionType.REPROCESS, id, record.client_id_ref, selectedClientId)
                      : undefined
                  }
                  retryLabel="Reprocess"
                  onDelete={
                    canDelete
                      ? (id) => handleDocumentAction(DocumentActionType.DELETE, id, record.client_id_ref)
                      : undefined
                  }
                />
              );
            }

            if (!isProcessing) {
              return (
                <DocumentActions
                  onDelete={
                    canDelete
                      ? (id) => handleDocumentAction(DocumentActionType.DELETE, id, record.client_id_ref)
                      : undefined
                  }
                />
              );
            }

            return null;
          }}
        />
      </PaginatedDatagrid>
    </>
  );
};
